import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Car, Clock, GripVertical, Lock, Settings2, ShieldAlert, User } from 'lucide-react'
import { useEffect, useState } from 'react'

export interface Order {
  id: string
  company_id: string
  customer_id: string
  vehicle_id: string
  plate: string
  customer_name: string
  customer_phone: string
  vehicle_model: string
  services: any[]
  extras: string[]
  total: number
  discount_amount: number
  coupon_code: string
  status: string
  payment_method: string
  assigned_to: string
  is_quote?: boolean
  created_at: string
  completed_at: string
  paid_at: string
  survey_sent: boolean
  os_number?: number
  stage_timestamps?: any
  stage_durations_minutes?: any
}

interface KanbanCardProps {
  order: Order
  isOverlay?: boolean
  collaborators?: { id: string; name: string }[]
  onCheckoutClick?: (order: Order) => void
  onStatusChange?: (order: Order, newStatus: string) => void
  onAssignCollaborator?: (orderId: string, collabId: string) => void
  onWhatsAppClick?: (order: Order) => void
  isCompact?: boolean
  currentUserRole?: string
}

const STATUS_ORDER = ['AGUARDANDO', 'LAVANDO', 'FINALIZANDO', 'CONCLUIDO']

export function KanbanCard({
  order,
  isOverlay,
  collaborators,
  onCheckoutClick,
  onStatusChange,
  onAssignCollaborator,
  onWhatsAppClick,
  isCompact,
  currentUserRole,
}: KanbanCardProps) {
  const [isEditingAssignee, setIsEditingAssignee] = useState(false)
  const [now, setNow] = useState(Date.now())

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 60000)
    return () => clearInterval(interval)
  }, [])

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: order.id,
    data: {
      type: 'Order',
      order,
    },
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  if (isDragging && !isOverlay) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className="h-32 bg-slate-800/50 rounded-lg border-2 border-dashed border-slate-600"
      />
    )
  }

  const timeAgo = formatDistanceToNow(new Date(order.created_at), {
    addSuffix: true,
    locale: ptBR,
  })

  const currentIdx = STATUS_ORDER.indexOf(order.status)
  const prevStatus = currentIdx > 0 ? STATUS_ORDER[currentIdx - 1] : null
  const nextStatus = currentIdx < STATUS_ORDER.length - 1 ? STATUS_ORDER[currentIdx + 1] : null

  // Lógica de restrição RBAC para Operadores (Colaborador)
  const isOperator = currentUserRole === 'COLABORADOR'
  const hasStarted = order.status !== 'AGUARDANDO'
  const isAssigneeLocked = isOperator && hasStarted

  // Cálculo de tempo dinâmico na etapa atual
  let timeInCurrentStage = 0
  if (order.stage_timestamps?.[order.status]) {
    const enteredAt = new Date(order.stage_timestamps[order.status]).getTime()
    timeInCurrentStage = Math.max(0, Math.floor((now - enteredAt) / 60000))
  }
  const totalTimeInStage = (order.stage_durations_minutes?.[order.status] || 0) + timeInCurrentStage

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={`
        relative group bg-slate-800 rounded-lg p-3 border shadow-sm flex flex-col gap-2
        ${isDragging ? 'opacity-40 border-blue-500 scale-95' : 'border-slate-700 hover:border-slate-600'}
        ${isOverlay ? 'shadow-2xl scale-105 border-blue-500 cursor-grabbing' : 'cursor-grab'}
        transition-all duration-200
      `}
    >
      <div
        {...listeners}
        className="absolute top-2 right-2 text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing hover:bg-slate-700 p-1 rounded-md"
      >
        <GripVertical size={16} />
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-start pr-6">
          <div className="font-bold text-lg text-slate-100 uppercase tracking-wide flex items-center gap-2">
            {order.os_number && (
              <span className="text-amber-500 text-sm bg-amber-500/10 px-2 py-0.5 rounded-md">
                #{String(order.os_number).padStart(3, '0')}
              </span>
            )}
            {order.plate || 'SEM PLACA'}
          </div>
        </div>

        <div className="flex items-center text-sm text-slate-300 gap-2 font-medium">
          <Car size={14} className="text-blue-400" />
          {order.vehicle_model || 'Veículo não informado'}
        </div>

        <div className="flex items-center text-sm text-slate-400 gap-2">
          <User size={14} className="text-emerald-400" />
          {order.customer_name || 'Cliente Avulso'}
        </div>

        {!isCompact && (
          <div className="mt-2 pt-2 border-t border-slate-700 flex flex-col gap-2">
            {/* Serviços */}
            <div className="flex flex-wrap gap-1">
              {Array.isArray(order.services) && order.services.length > 0 ? (
                order.services.map((svc: any, i: number) => (
                  <span
                    key={i}
                    className="text-[10px] uppercase font-bold px-2 py-1 bg-slate-900/50 text-slate-300 rounded-md"
                  >
                    {typeof svc === 'string' ? svc : svc.name || 'Serviço'}
                  </span>
                ))
              ) : (
                <span className="text-xs text-slate-500 italic">Nenhum serviço listado</span>
              )}
            </div>

            {/* Atribuição de Equipe */}
            <div
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                if (isAssigneeLocked) {
                  alert(
                    'Você não pode alterar o responsável de um veículo que já saiu da Fila de Espera. Peça autorização a um Administrador.'
                  )
                  return
                }
                if (!isOverlay && onAssignCollaborator && !isEditingAssignee) {
                  setIsEditingAssignee(true)
                }
              }}
              className={`flex items-center justify-between bg-slate-900/40 rounded p-1.5 border relative z-10 ${
                isAssigneeLocked
                  ? 'border-red-500/30 cursor-not-allowed opacity-80'
                  : !isOverlay && onAssignCollaborator && !isEditingAssignee
                    ? 'border-slate-700/50 cursor-pointer hover:bg-slate-800 transition-colors'
                    : 'border-slate-700/50'
              }`}
            >
              {isEditingAssignee && !isAssigneeLocked ? (
                <select
                  className="w-full text-xs bg-slate-800 text-slate-100 border border-blue-500 rounded p-1 outline-none"
                  value={order.assigned_to || ''}
                  onBlur={() => setIsEditingAssignee(false)}
                  onChange={(e) => {
                    onAssignCollaborator?.(order.id, e.target.value)
                    setIsEditingAssignee(false)
                  }}
                  onPointerDown={(e) => e.stopPropagation()} // Impede o drag
                >
                  <option value="">Sem Responsável</option>
                  {collaborators?.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              ) : (
                <>
                  <div className="flex items-center gap-1.5 overflow-hidden w-full">
                    <ShieldAlert
                      size={12}
                      className={
                        order.assigned_to
                          ? 'text-blue-400 flex-shrink-0'
                          : 'text-slate-500 flex-shrink-0'
                      }
                    />
                    <span
                      className={`text-[11px] font-semibold truncate ${order.assigned_to ? 'text-blue-400' : 'text-slate-500 italic'}`}
                    >
                      {order.assigned_to
                        ? collaborators?.find((c) => c.id === order.assigned_to)?.name ||
                          'Desconhecido'
                        : 'Atribuir Responsável'}
                    </span>
                  </div>
                  {!isOverlay && onAssignCollaborator && (
                    <div
                      className={`p-1 rounded flex-shrink-0 ${isAssigneeLocked ? 'text-red-400' : 'text-slate-400'}`}
                    >
                      {isAssigneeLocked ? <Lock size={12} /> : <Settings2 size={12} />}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {!isCompact && (
          <div className="mt-2 flex items-center justify-between">
            <div className="flex flex-col text-xs text-slate-500 gap-0.5 font-medium">
              <div className="flex items-center gap-1.5">
                <Clock size={12} />
                Chegou {timeAgo}
              </div>
              <div className="text-[10px] text-slate-400">
                Tempo na etapa: {totalTimeInStage} min
              </div>
            </div>
            <div className="font-bold text-emerald-400">R$ {order.total?.toFixed(2) || '0.00'}</div>
          </div>
        )}
        {isCompact && (
          <div className="flex justify-between items-center mt-1">
            <span className="text-[10px] text-slate-500">
              {order.assigned_to
                ? collaborators?.find((c) => c.id === order.assigned_to)?.name?.split(' ')[0] ||
                  'Atribuído'
                : 'Sem responsável'}
            </span>
            <span className="font-bold text-emerald-400 text-xs">
              R$ {order.total?.toFixed(2) || '0.00'}
            </span>
          </div>
        )}

        <div className="mt-3 flex items-center justify-between gap-2">
          {prevStatus ? (
            <button
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation()
                onStatusChange?.(order, prevStatus)
              }}
              className="flex-1 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs font-semibold rounded-md transition-colors"
            >
              &larr; Voltar
            </button>
          ) : (
            <div className="flex-1" />
          )}

          {nextStatus ? (
            <button
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation()
                onStatusChange?.(order, nextStatus)
              }}
              className="flex-1 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold rounded-md transition-colors"
            >
              Avançar &rarr;
            </button>
          ) : (
            <div className="flex-1" />
          )}
        </div>

        {/* WhatsApp Button */}
        {['SUPERADMIN', 'ADMIN', 'ATENDENTE'].includes(currentUserRole || '') &&
          onWhatsAppClick && (
            <button
              onPointerDown={(e) => {
                e.stopPropagation()
              }}
              onClick={(e) => {
                if (order.status === 'CONCLUIDO') {
                  e.stopPropagation()
                  onWhatsAppClick(order)
                }
              }}
              disabled={order.status !== 'CONCLUIDO'}
              title={
                order.status !== 'CONCLUIDO'
                  ? 'Disponível apenas quando o veículo estiver Concluído'
                  : 'Avisar cliente no WhatsApp'
              }
              className={`mt-2 w-full flex items-center justify-center gap-2 font-semibold py-1.5 rounded-md text-xs transition-colors cursor-pointer relative z-10 ${
                order.status === 'CONCLUIDO'
                  ? 'bg-[#00a884] hover:bg-[#008f6f] text-white'
                  : 'bg-slate-700/50 text-slate-500 cursor-not-allowed border border-slate-700'
              }`}
            >
              Avisar Whats
            </button>
          )}

        {order.status === 'CONCLUIDO' && onCheckoutClick && (
          <button
            onPointerDown={(e) => {
              e.stopPropagation()
            }}
            onClick={(e) => {
              e.stopPropagation()
              onCheckoutClick(order)
            }}
            className="mt-3 w-full bg-emerald-600 hover:bg-emerald-500 text-white font-semibold py-2 rounded-md text-sm transition-colors cursor-pointer relative z-10"
          >
            💰 Finalizar e Pagar
          </button>
        )}
      </div>
    </div>
  )
}
